// Text matching + highlighting helpers for the command palette (and other pickers).
import { Fragment, type ReactNode } from 'react';

/** Lowercased, whitespace-separated search words. */
export function tokenize(query: string): string[] {
  return query.toLowerCase().split(/\s+/).filter(Boolean);
}

/** True when every word occurs somewhere in one of the texts (case-insensitive). */
export function matchesAll(words: string[], ...texts: (string | null | undefined)[]): boolean {
  if (words.length === 0) return true;
  const hay = texts
    .filter(Boolean)
    .join('\u0001')
    .toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** Lower is better: exact > prefix > word-start > anywhere. */
export function matchScore(text: string, words: string[]): number {
  if (words.length === 0) return 3;
  const t = text.toLowerCase();
  const q = words.join(' ');
  if (t === q) return 0;
  if (t.startsWith(q)) return 1;
  const first = words[0];
  if (t.startsWith(first) || t.includes(` ${first}`)) return 2;
  return 3;
}

/** Renders `text` with every occurrence of the search words wrapped in <mark>. */
export function Highlight({ text, words }: { text: string; words: string[] }): ReactNode {
  if (!words.length || !text) return text;
  const lower = text.toLowerCase();
  const marked = new Uint8Array(text.length);
  let any = false;
  for (const w of words) {
    let i = lower.indexOf(w);
    while (i !== -1 && w.length > 0) {
      marked.fill(1, i, i + w.length);
      any = true;
      i = lower.indexOf(w, i + w.length);
    }
  }
  if (!any) return text;
  const parts: ReactNode[] = [];
  let start = 0;
  for (let i = 1; i <= text.length; i++) {
    if (i === text.length || marked[i] !== marked[start]) {
      const seg = text.slice(start, i);
      parts.push(marked[start] ? <mark key={start}>{seg}</mark> : <Fragment key={start}>{seg}</Fragment>);
      start = i;
    }
  }
  return <>{parts}</>;
}
